import React, { useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button, Checkbox, CloseIcon, Modal } from 'examkrackers-components'
import { useWindowDimensions } from 'hooks/useWindowDimensions'
import warning from 'assets/warning.svg'
import { markSrqStartModalSeen } from 'services/AuthService'
import { refreshStudentProfile } from './useOpenSrqQuizFlow'
import { quizModalMobileMaxWidthPx } from './QuizModal.styles'

const howToUseBodyKeys = [
  'aiSpaceRepetition.howToUseP1',
  'aiSpaceRepetition.howToUseP2',
  'aiSpaceRepetition.howToUseP3',
  'aiSpaceRepetition.howToUseP4',
  'aiSpaceRepetition.howToUseP5',
  'aiSpaceRepetition.howToUseP6'
] as const

const desktopModalStyle = {
  overlay: { zIndex: 10000000 },
  content: {
    zIndex: 10000001,
    boxSizing: 'border-box' as const,
    width: 'min(680px, calc(100vw - 32px))',
    minWidth: 'min(680px, calc(100vw - 32px))',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'min(700px, calc(100vh - 32px))',
    padding: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    overflow: 'visible' as const,
    border: '2px solid #F8961A',
    borderRadius: '8px',
    boxShadow: '0px 4px 12px 0px #0000001A',
    background: '#fff',
    position: 'relative' as const
  }
}

type SpaceRepetitionModalProps = {
  isOpen: boolean
  onClose: () => void
  setQuizModalOpen?: () => void
  isInfoOnly?: boolean
}

const SpaceRepetitionModal: React.FC<SpaceRepetitionModalProps> = ({
  isOpen,
  onClose,
  setQuizModalOpen,
  isInfoOnly = false
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { width } = useWindowDimensions()
  const isMobileLayout = width <= quizModalMobileMaxWidthPx
  const [dontShowAgain, setDontShowAgain] = useState(false)

  const modalStyle = useMemo(() => {
    if (!isMobileLayout) {
      return desktopModalStyle
    }

    return {
      overlay: {
        zIndex: 10000000,
        width: '100%',
        maxWidth: '100%',
        height: '100dvh',
        minHeight: '100dvh',
        padding: '0.5dvh 2.5vw',
        boxSizing: 'border-box' as const,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      content: {
        zIndex: 10000001,
        boxSizing: 'border-box' as const,
        position: 'relative' as const,
        inset: 'unset' as const,
        top: 'auto',
        left: 'auto',
        right: 'auto',
        bottom: 'auto',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        height: '98%',
        maxHeight: '98%',
        minHeight: 0,
        margin: 0,
        padding: 0,
        transform: 'none',
        overflow: 'hidden' as const,
        border: '2px solid #F8961A',
        borderRadius: '8px',
        boxShadow: 'none',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column' as const
      }
    }
  }, [isMobileLayout])

  const handleDontShowAgainChange = () => {
    setDontShowAgain(previous => !previous)
  }

  const handleDontShowAgainLabelKeyDown = (
    event: React.KeyboardEvent<HTMLSpanElement>
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    handleDontShowAgainChange()
  }

  const handleStartQuiz = async () => {
    if (!setQuizModalOpen) {
      return
    }

    if (dontShowAgain) {
      try {
        await markSrqStartModalSeen()
        await refreshStudentProfile(dispatch)
      } catch {
        // Continue to quiz even if preference save fails.
      }
    }

    setQuizModalOpen()
  }

  return (
    <Modal
      data-testid='SpacedRepetitionSectionQuiz-Modal'
      open={isOpen}
      handleClose={onClose}
      blank
      style={modalStyle}
    >
      <ModalInner>
        <CloseButton
          type='button'
          aria-label={t('aiSpaceRepetition.closeInfoModal')}
          onClick={onClose}
        >
          <CloseIcon />
        </CloseButton>
        <ScrollArea>
          <ScrollContent>
            <WarningIcon src={warning} alt='' aria-hidden='true' />
            <Title>{t('aiSpaceRepetition.howToUseTitle')}</Title>
            <Body>
              {howToUseBodyKeys?.map(key => (
                <p key={key}>{t(key)}</p>
              ))}
            </Body>
          </ScrollContent>
        </ScrollArea>
        <Footer>
          <FooterContent>
            <ButtonsWrapper $isInfoOnly={isInfoOnly}>
              <StyledButton
                data-testid='SpacedRepetitionSectionQuiz-cancel'
                type='button'
                onClick={onClose}
                color='tertiary'
                size='small'
              >
                {t('aiSpaceRepetition.cancel')}
              </StyledButton>
              {!isInfoOnly ? (
                <StyledButton
                  data-testid='SpacedRepetitionSectionQuiz-save'
                  type='button'
                  color='secondary'
                  size='small'
                  onClick={handleStartQuiz}
                >
                  {t('aiSpaceRepetition.save')}
                </StyledButton>
              ) : null}
            </ButtonsWrapper>
            {!isInfoOnly ? (
              <DontShowAgain>
                <Checkbox
                  name='dont-show-how-to-use-srq-again'
                  isSelected={dontShowAgain}
                  onChange={handleDontShowAgainChange}
                />
                <DontShowAgainText
                  role='button'
                  tabIndex={0}
                  aria-label={t('aiSpaceRepetition.dontShowAgain')}
                  onClick={handleDontShowAgainChange}
                  onKeyDown={handleDontShowAgainLabelKeyDown}
                >
                  {t('aiSpaceRepetition.dontShowAgain')}
                </DontShowAgainText>
              </DontShowAgain>
            ) : null}
          </FooterContent>
        </Footer>
      </ModalInner>
    </Modal>
  )
}

export default SpaceRepetitionModal

const ModalInner = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  max-height: min(700px, calc(100vh - 32px));
  min-height: 0;
  padding: 30px 0;
  position: relative;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    width: 100%;
    height: 100%;
    max-height: 100%;
    min-height: 0;
  }
`

const horizontalContentPadding = css`
  padding-left: 80px;
  padding-right: 80px;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    padding-left: 24px;
    padding-right: 24px;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1a1a1a;
  cursor: pointer;
  z-index: 2;

  svg {
    width: 14px;
    height: 14px;
  }

  &:focus-visible {
    outline: 2px solid #f8961a;
    outline-offset: 2px;
  }
`

const ScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
`

const ScrollContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  ${horizontalContentPadding}
`

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  ${horizontalContentPadding}
`

const WarningIcon = styled.img`
  display: block;
  width: 42px;
  height: 42px;
  margin: 0 0 24px;
`

const Title = styled.h2`
  margin: 0 0 20px;
  font-family: Arial, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.3;
  text-align: center;
  color: #1a1a1a;
`

const Body = styled.div`
  width: 100%;
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  color: #444444;

  p {
    margin: 0 0 16px;
  }

  p:last-child {
    margin-bottom: 0;
  }
`

const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 24px;
  background: #fff;
`

const ButtonsWrapper = styled.div<{ $isInfoOnly: boolean }>`
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 16px;
  width: 100%;
  margin: ${({ $isInfoOnly }) => ($isInfoOnly ? '0' : '0 0 16px')};

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    flex-direction: column;
  }
`

const StyledButton = styled(Button)`
  min-width: 140px;

  @media (max-width: ${quizModalMobileMaxWidthPx}px) {
    width: 100%;
    min-width: 0;
  }
`

const DontShowAgain = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-family: Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.2;
  color: #444444;
`

const DontShowAgainText = styled.span`
  cursor: pointer;
`
